import { TooltipProvider } from '@radix-ui/react-tooltip'
import React from 'react'

import { styled } from '~/styled'

import { Dismissible } from '../dismissible'
import {
  SectionMessageProvider,
  useSectionMessageContext
} from './SectionMessageContext'
import { SectionMessageDescription } from './SectionMessageDescription'
import { SectionMessageDismiss } from './SectionMessageDismiss'
import { SectionMessageIcon } from './SectionMessageIcon'
import {
  SectionMessageActions,
  SectionMessageContent
} from './SectionMessageLayout'
import { SectionMessageTitle } from './SectionMessageTitle'

const StyledSectionMessage = styled(Dismissible, {
  base: [
    'relative',
    'rounded-md',
    'flex',
    'shrink-0',
    'min-h-12',
    'p-4',
    'border',
    'border-white'
  ],
  variants: {
    theme: {
      success: ['bg-success-light', 'text-success-dark'],
      warning: ['bg-warning-light', 'text-warning-text'],
      error: ['bg-danger-light', 'text-danger-dark'],
      neutral: ['bg-grey-100', 'text-grey-1000'],
      info: ['bg-info-light', 'text-info-dark']
    },
    hasIcon: {
      true: ['pl-10']
    },
    hasDismiss: {
      true: ['pr-12']
    }
  }
})

const SectionMessageRoot = ({
  children,
  ...rest
}: React.ComponentProps<typeof StyledSectionMessage> & {
  className?: string
}) => {
  const { theme, hasIcon, hasDismiss } = useSectionMessageContext()

  return (
    <StyledSectionMessage
      {...rest}
      theme={theme}
      hasIcon={hasIcon}
      hasDismiss={hasDismiss}
    >
      <div className="flex grow justify-between gap-3">{children}</div>
    </StyledSectionMessage>
  )
}

export type SectionMessageTheme =
  | 'success'
  | 'warning'
  | 'error'
  | 'neutral'
  | 'info'

export type SectionMessageProps = React.ComponentProps<
  typeof SectionMessageRoot
> & {
  theme?: SectionMessageTheme
}

export const SectionMessage = ({
  theme = 'info',
  ...rest
}: SectionMessageProps) => (
  <TooltipProvider>
    <SectionMessageProvider theme={theme}>
      <SectionMessageRoot {...rest} />
    </SectionMessageProvider>
  </TooltipProvider>
)

SectionMessage.Title = SectionMessageTitle
SectionMessage.Description = SectionMessageDescription
SectionMessage.Icon = SectionMessageIcon
SectionMessage.Dismiss = SectionMessageDismiss
SectionMessage.Content = SectionMessageContent
SectionMessage.Actions = SectionMessageActions
